import {
  isOfflineAndroid,
  createRequestId,
  ensureCallbackRegistry
} from './androidOfflineCart';


/**
 * Save delivery addresses received from backend into Room.
 *
 * Android:
 *
 * saveOfflineDeliveryAddresses(
 *   requestId,
 *   responseJson
 * )
 */
const saveOfflineDeliveryAddresses = ({
  responseJson
}) => {

  return new Promise((resolve, reject) => {

    if (!isOfflineAndroid()) {
      reject(
        new Error(
          'Android POS offline bridge unavailable'
        )
      );
      return;
    }

    if (
      typeof window.AndroidPOS
        .saveOfflineDeliveryAddresses !==
      'function'
    ) {
      reject(
        new Error(
          'AndroidPOS.saveOfflineDeliveryAddresses is not available'
        )
      );
      return;
    }

    if (!responseJson) {
      reject(
        new Error(
          'responseJson is required'
        )
      );
      return;
    }

    const requestId =
      createRequestId(
        'save_offline_delivery_addresses'
      );

    ensureCallbackRegistry();

    window.__orderArtOfflineCallbacks[requestId] = {
      resolve,
      reject
    };

    try {

      window.AndroidPOS.saveOfflineDeliveryAddresses(
        requestId,
        String(responseJson)
      );

    } catch (error) {

      delete window.__orderArtOfflineCallbacks[
        requestId
      ];

      reject(error);

      return;
    }

    setTimeout(() => {

      if (
        window.__orderArtOfflineCallbacks[requestId]
      ) {

        delete window.__orderArtOfflineCallbacks[
          requestId
        ];

        reject(
          new Error(
            `Offline save delivery addresses timeout: ${requestId}`
          )
        );
      }

    }, 10000);
  });
};


/**
 * Get delivery addresses from Room.
 *
 * Android:
 *
 * getOfflineDeliveryAddresses(
 *   requestId,
 *   localCustomerId
 * )
 */
const getOfflineDeliveryAddresses = ({
  localCustomerId
}) => {

  return new Promise((resolve, reject) => {

    if (!isOfflineAndroid()) {
      reject(
        new Error(
          'Android POS offline bridge unavailable'
        )
      );
      return;
    }

    if (
      typeof window.AndroidPOS
        .getOfflineDeliveryAddresses !==
      'function'
    ) {
      reject(
        new Error(
          'AndroidPOS.getOfflineDeliveryAddresses is not available'
        )
      );
      return;
    }

    if (!localCustomerId) {
      reject(
        new Error(
          'localCustomerId is required'
        )
      );
      return;
    }

    const requestId =
      createRequestId(
        'get_offline_delivery_addresses'
      );

    ensureCallbackRegistry();

    window.__orderArtOfflineCallbacks[requestId] = {
      resolve,
      reject
    };

    try {

      window.AndroidPOS.getOfflineDeliveryAddresses(
        requestId,
        String(localCustomerId)
      );

    } catch (error) {

      delete window.__orderArtOfflineCallbacks[
        requestId
      ];

      reject(error);

      return;
    }

    setTimeout(() => {

      if (
        window.__orderArtOfflineCallbacks[requestId]
      ) {

        delete window.__orderArtOfflineCallbacks[
          requestId
        ];

        reject(
          new Error(
            `Offline get delivery addresses timeout: ${requestId}`
          )
        );
      }

    }, 10000);
  });
};


const updateOfflineOrderAddress = ({
  orderId,
  localAddressId,
  localCustomerId
}) => {

  return new Promise((resolve, reject) => {

    if (!isOfflineAndroid()) {
      reject(
        new Error(
          'Android POS offline bridge unavailable'
        )
      );
      return;
    }

    if (
      typeof window.AndroidPOS
        .updateOfflineOrderAddress !==
      'function'
    ) {
      reject(
        new Error(
          'AndroidPOS.updateOfflineOrderAddress is not available'
        )
      );
      return;
    }

    if (!orderId) {
      reject(
        new Error(
          'orderId is required'
        )
      );
      return;
    }

    if (!localAddressId) {
      reject(
        new Error(
          'localAddressId is required'
        )
      );
      return;
    }

    const requestId =
      createRequestId(
        'update_offline_order_address'
      );

    ensureCallbackRegistry();

    window.__orderArtOfflineCallbacks[
      requestId
    ] = {
      resolve,
      reject
    };

    try {

      window.AndroidPOS.updateOfflineOrderAddress(
        requestId,
        String(orderId),
        String(localAddressId),
        localCustomerId
          ? String(localCustomerId)
          : ''
      );

    } catch (error) {

      delete window.__orderArtOfflineCallbacks[
        requestId
      ];

      reject(error);

      return;
    }

    setTimeout(() => {

      if (
        window.__orderArtOfflineCallbacks[
          requestId
        ]
      ) {

        delete window.__orderArtOfflineCallbacks[
          requestId
        ];

        reject(
          new Error(
            `Offline update address timeout: ${requestId}`
          )
        );
      }

    }, 10000);
  });
};

const addOfflineDeliveryAddress = ({
  localCustomerId,
  addressData
}) => {

  return new Promise((resolve, reject) => {

    // -------------------------------------------------
    // VALIDATE ANDROID
    // -------------------------------------------------

    if (!isOfflineAndroid()) {
      reject(
        new Error(
          'Android POS offline bridge unavailable'
        )
      );
      return;
    }

    if (
      typeof window.AndroidPOS
        .addOfflineDeliveryAddress !== 'function'
    ) {
      reject(
        new Error(
          'AndroidPOS.addOfflineDeliveryAddress is not available'
        )
      );
      return;
    }

    // -------------------------------------------------
    // VALIDATE CUSTOMER
    // -------------------------------------------------

    if (!localCustomerId) {
      reject(
        new Error(
          'localCustomerId is required'
        )
      );
      return;
    }

    // -------------------------------------------------
    // VALIDATE ADDRESS
    // -------------------------------------------------

    if (
      !addressData ||
      typeof addressData !== 'object'
    ) {
      reject(
        new Error(
          'addressData is required'
        )
      );
      return;
    }

    // -------------------------------------------------
    // CREATE REQUEST
    // -------------------------------------------------

    const requestId = createRequestId(
      'add_offline_delivery_address'
    );

    ensureCallbackRegistry();

    window.__orderArtOfflineCallbacks[requestId] = {
      resolve,
      reject
    };

    console.log(
      '[OFFLINE_DELIVERY_ADDRESS] Adding address',
      {
        requestId,
        localCustomerId,
        addressData
      }
    );

    // -------------------------------------------------
    // CALL ANDROID
    // -------------------------------------------------

    try {

      window.AndroidPOS.addOfflineDeliveryAddress(
        requestId,
        String(localCustomerId),
        JSON.stringify(addressData)
      );

    } catch (error) {

      delete window.__orderArtOfflineCallbacks[
        requestId
      ];

      reject(error);

      return;
    }

    // -------------------------------------------------
    // TIMEOUT
    // -------------------------------------------------

    setTimeout(() => {

      if (
        window.__orderArtOfflineCallbacks[requestId]
      ) {

        delete window.__orderArtOfflineCallbacks[
          requestId
        ];

        console.error(
          '[OFFLINE_DELIVERY_ADDRESS] Bridge timeout',
          {
            requestId,
            localCustomerId
          }
        );

        reject(
          new Error(
            `Add offline delivery address timeout: ${requestId}`
          )
        );
      }

    }, 10000);
  });
};

export {
  saveOfflineDeliveryAddresses,
  getOfflineDeliveryAddresses,
  updateOfflineOrderAddress,
  addOfflineDeliveryAddress
};
